import $ from 'jquery';

import CommonData from 'app/src/data/CommonData';
import Format from 'app/src/data/Format';
import Payment from 'app/src/data/models/Payment';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import Localize from 'app/src/localize/Localize';
import PaymentServiceTerminal from 'app/src/payments/terminals/PaymentServiceTerminal';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';

const _DESCRIPTION = 'Vantiv triPOS Payment Terminal';

// This class is an abstract implementation, we can't instantiate this directly, we need to choose from one of these:
// - app/src/payments/terminals/LightspeedPaymentsTerminal

export default class VantivTriPOSTerminal extends PaymentServiceTerminal {
  constructor(terminalModel) {
    super(terminalModel);
  }

  /**
   * Returns a string describing the terminal.
   *
   * @returns {string}    A description of the terminal
   */
  description() {
    return _DESCRIPTION;
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  Optional - when true the terminal will be prompt for a manual
   *                                 entry (when available)
   */
  processPayment(paymentOptions) {
    let deferred = $.Deferred();

    this.stagePayment(paymentOptions)
      .done(({ payment, terminalPayment }) => {
        this.pollApiForPaymentStatus(payment)
          .done(() => {
            const paymentStatus = payment.get('status');
            const paymentTransaction = payment.paymentTransaction;

            // Save the model for later
            terminalPayment.serviceModel = payment;
            terminalPayment.model.set(
              'amount',
              Format.parseDecimal(payment.get('totalAmount'), terminalPayment.amount)
            );

            switch (paymentStatus) {
              case Payment.Status.APPROVED:
                deferred.resolve(
                  new TerminalTransactionResponse(
                    Terminal.Response.APPROVED,
                    paymentStatus,
                    terminalPayment
                  )
                );
                break;

              default:
                const paymentResponseMessage = paymentTransaction
                  ? paymentTransaction.get('message') || ''
                  : '';

                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.TERMINAL_ERROR,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;
            }
          })
          .fail(() => {
            const paymentStatus = payment.get('status');
            const paymentTransaction = payment.paymentTransaction;
            const paymentResponseMessage = paymentTransaction
              ? paymentTransaction.get('message') || ''
              : '';

            terminalPayment.serviceModel = payment;

            // map the payment service status to the terminal response
            switch (paymentStatus) {
              case Payment.Status.DECLINED:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.DECLINED,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case Payment.Status.ERROR:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.TERMINAL_ERROR,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case Payment.Status.CANCELLED:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.CANCELLED_FROM_TERMINAL,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;

              case Payment.Status.UNKNOWN:
                deferred.reject(
                  new TerminalTransactionResponse(
                    Terminal.Response.UNKNOWN,
                    paymentStatus,
                    terminalPayment,
                    paymentResponseMessage
                  )
                );
                break;
            }
          });
      })
      .fail((transactionResponse) => {
        deferred.reject(transactionResponse);
      });

    return deferred.promise();
  }

  /**
   * Communicates with the terminal to cancel the request.
   */
  cancelTransaction() {
    let deferred = $.Deferred();

    // cleanup
    if (this._pollingTimer) {
      clearTimeout(this._pollingTimer);
      delete this._pollingTimer;
    }

    // this terminal type does not support cancelling via the POS
    deferred.reject();

    return deferred.promise();
  }

  /**
   * Communicates with the terminal to check the status of the terminal.  Resolves with the appropriate
   * status.
   */
  checkStatus() {
    let deferred = $.Deferred();

    // There is no status check for a TriPOS terminal so just assume it's online
    // and resolve the promise immediately
    deferred.resolve(Terminal.Status.ONLINE);

    return deferred.promise();
  }
}
